import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  DEFAULT_COMPANY_SETTINGS,
  type CallActivityDto,
  type LiveEmployee,
  type PointDelivery,
  type RouteDto,
} from '@geotracker/contracts';
import { api } from '@/lib/api/client';
import { useCompanySettings } from '@/lib/api/settings';
import { Card } from '@/components/ui/card';
import { Dot } from '@/components/ui/badge';
import { ErrorState, Loading } from '@/components/ui/state';
import { cn } from '@/lib/utils';
import { useStoredBasemap } from './basemaps';
import { EmployeeDetail } from './employee-detail';
import { FleetSummary } from './fleet-summary';
import { MapView, type ColourBy, type IndexRange } from './map-view';
import { Replay } from './replay';
import { Roster } from './roster';

const REFRESH_MS = 15_000;

export function LiveMapPage() {
  const [selected, setSelected] = useState<number | null>(null);
  const [live, setLive] = useState(true);

  /**
   * The day whose trail is shown, empty meaning "today".
   *
   * Left blank rather than pre-filled with the browser's date, because the
   * server decides what "today" is in the company's timezone. The panel and the
   * phone may sit in different ones, and letting the browser guess reintroduces
   * exactly the off-by-a-day this fixes.
   */
  const [date, setDate] = useState('');
  const [seekTo, setSeekTo] = useState<string | null>(null);
  const [replayIndex, setReplayIndex] = useState<number | null>(null);
  const [focusStop, setFocusStop] = useState<number | null>(null);
  const [focusRange, setFocusRange] = useState<IndexRange | null>(null);

  /**
   * Which axis colours the trail, and which delivery classes are drawn.
   *
   * Held here rather than inside the map because the detail panel drives both:
   * clicking "Backfilled 42" under the Data tab has to switch the map's colour
   * mode and filter it in one gesture, and a control that cannot change what
   * the map shows is a label, not a filter.
   *
   * An empty filter set means "everything" — see the map's DeliveryFilter.
   */
  const [colourBy, setColourBy] = useState<ColourBy>('movement');
  const [deliveryFilter, setDeliveryFilter] = useState<ReadonlySet<PointDelivery>>(new Set());

  const settingsQuery = useCompanySettings();
  const settings = settingsQuery.data;
  const [basemap, setBasemap] = useStoredBasemap(
    settings?.maps.defaultBasemap ?? 'osm',
    Boolean(settings?.maps.googleMapsBrowserKey),
  );

  const liveQuery = useQuery({
    queryKey: ['live'],
    queryFn: () => api.get<LiveEmployee[]>('/admin/live/employees'),
    // Polling rather than a socket: the server throttles live updates to one
    // per employee per 10s anyway, so this costs one request and no extra
    // infrastructure. Socket.IO arrives with the realtime dashboard.
    refetchInterval: live ? REFRESH_MS : false,
  });

  const routeQuery = useQuery({
    queryKey: ['route', selected, date],
    queryFn: () =>
      api.get<RouteDto>(`/admin/employees/${selected}/route${date ? `?date=${date}` : ''}`),
    enabled: selected !== null,
  });

  const callsQuery = useQuery({
    queryKey: ['calls', selected, date],
    queryFn: () =>
      api.get<CallActivityDto>(`/admin/employees/${selected}/calls${date ? `?date=${date}` : ''}`),
    enabled: selected !== null,
  });

  const employees = useMemo(() => liveQuery.data?.data ?? [], [liveQuery.data]);
  const route = routeQuery.data?.data ?? null;
  const selectedEmployee = employees.find((e) => e.employee.id === selected) ?? null;

  // An employee who leaves the roster (tracking disabled, employment ended)
  // must not leave the detail panel showing a person who is no longer there.
  useEffect(() => {
    if (selected !== null && employees.length > 0 && !selectedEmployee) setSelected(null);
  }, [selected, selectedEmployee, employees.length]);

  const handleSeek = useCallback((iso: string) => setSeekTo(iso), []);
  const handleFocusStop = useCallback((index: number) => {
    // Reset first so clicking the same stop twice pans to it again.
    setFocusStop(null);
    requestAnimationFrame(() => setFocusStop(index));
  }, []);
  const handleFocusRange = useCallback((range: IndexRange) => {
    // Same reset-then-set as the stop focus: the map frames on a change of
    // identity, so re-clicking the same row must produce a new object.
    setFocusRange(null);
    requestAnimationFrame(() => setFocusRange({ ...range }));
  }, []);

  /**
   * Switches the map to delivery colouring, optionally isolating one class.
   *
   * Called from the Data tab. Passing a class filters to it; passing nothing
   * clears the filter, because "colour the map by this" means show me all of it
   * in these colours, not show me one third of it.
   */
  const handleShowDelivery = useCallback((only?: PointDelivery) => {
    setColourBy('delivery');
    setDeliveryFilter(only ? new Set([only]) : new Set());
  }, []);

  if (liveQuery.isPending) return <Loading label="Loading live positions…" />;
  if (liveQuery.error) {
    return <ErrorState error={liveQuery.error} onRetry={() => void liveQuery.refetch()} />;
  }

  return (
    <section className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Live tracking</h1>
          <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
            <Dot tone={live ? 'success' : 'muted'} pulse={live} />
            {live ? 'Live · refreshes every 15s' : 'Paused'}
            {liveQuery.isFetching ? ' · updating…' : ''}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/*
           * A pause control, because the map re-rendering under a cursor that
           * is halfway through reading a popup is the single most annoying
           * thing a polling dashboard does.
           */}
          <button
            type="button"
            onClick={() => setLive((v) => !v)}
            className="rounded-lg border border-border px-3 py-1.5 text-sm transition-colors hover:bg-muted"
          >
            {live ? 'Pause updates' : 'Resume updates'}
          </button>

          {selected !== null ? (
            <>
              <input
                type="date"
                value={date || route?.date || ''}
                onChange={(e) => setDate(e.target.value)}
                className="rounded-lg border border-border bg-background px-2 py-1.5 text-sm"
                aria-label="Route date"
              />
              {date ? (
                <button
                  type="button"
                  onClick={() => setDate('')}
                  className="rounded-lg border border-border px-3 py-1.5 text-sm transition-colors hover:bg-muted"
                >
                  Today
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => {
                  setSelected(null);
                  setDate('');
                  setReplayIndex(null);
                }}
                className="rounded-lg border border-border px-3 py-1.5 text-sm transition-colors hover:bg-muted"
              >
                Clear selection
              </button>
            </>
          ) : null}
        </div>
      </header>

      <FleetSummary employees={employees} />

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
        <Card className="flex flex-col overflow-hidden p-0">
          <MapView
            employees={employees}
            selectedId={selected}
            onSelect={setSelected}
            route={route}
            replayIndex={replayIndex}
            focusStop={focusStop}
            focusRange={focusRange}
            settings={settings?.maps ?? DEFAULT_COMPANY_SETTINGS.maps}
            basemap={basemap}
            onBasemapChange={setBasemap}
            colourBy={colourBy}
            onColourByChange={setColourBy}
            deliveryFilter={deliveryFilter}
            onDeliveryFilterChange={setDeliveryFilter}
            className={cn('h-[420px] rounded-none border-0 lg:h-[560px]')}
          />
          {route && route.points.length > 1 ? (
            <Replay route={route} seekTo={seekTo} onIndexChange={setReplayIndex} />
          ) : null}
        </Card>

        <Card className="flex max-h-[560px] flex-col overflow-hidden p-0">
          <Roster employees={employees} selectedId={selected} onSelect={setSelected} />
        </Card>
      </div>

      {selectedEmployee ? (
        <EmployeeDetail
          employee={selectedEmployee}
          route={route}
          routeLoading={routeQuery.isPending}
          calls={callsQuery.data?.data ?? null}
          onSeek={handleSeek}
          onFocusStop={handleFocusStop}
          onFocusRange={handleFocusRange}
          onShowDelivery={handleShowDelivery}
        />
      ) : (
        <Card className="p-8 text-center">
          <p className="text-sm font-medium">Select an employee</p>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
            Pick someone from the map or the list to see their route for the day, where they
            stopped and for how long, their speed and heading, and their call activity.
          </p>
        </Card>
      )}
    </section>
  );
}
